import { useState, useMemo, useRef, useEffect } from 'react';
import { MdClose } from 'react-icons/md';

function dayLabel(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Danas';
  if (date.toDateString() === yesterday.toDateString()) return 'Juče';
  return date.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getDisplayContent(content) {
  if (!content) return '';
  if (content.startsWith('data:image')) return '🖼 Slika';
  return content;
}

function highlightMatch(text, query) {
  if (!query || !text) return text;
  if (text.startsWith('data:image')) return '🖼 Slika';
  const q = query.trim().toLowerCase();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-primary-200 dark:bg-primary-800 rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export default function ChatSearchModal({
  isOpen,
  onClose,
  messages = [],
  currentUserId,
  senderDisplayName,
  onScrollToMessage,
  t = (s) => s,
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => {
      const c = m.content || '';
      if (c.startsWith('data:image')) return 'slika'.includes(q) || 'image'.includes(q);
      return c.toLowerCase().includes(q);
    });
  }, [messages, query]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleSelect = (msg) => {
    onScrollToMessage?.(msg);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-20" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('conversations.searchInChat') || 'Pretraži u razgovoru...'}
            className="flex-1 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400"
            autoFocus
          />
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
            aria-label="Zatvori"
          >
            <MdClose className="text-xl" />
          </button>
        </div>
        <div ref={listRef} className="max-h-80 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {query.trim() ? (t('conversations.noSearchResults') || 'Nema rezultata') : (t('conversations.enterSearch') || 'Unesite pojam za pretragu')}
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((msg) => (
                <li key={msg.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(msg)}
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50"
                  >
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                      {msg.senderId === currentUserId ? 'Vi' : senderDisplayName} · {dayLabel(msg.createdAt)}
                    </p>
                    <p className="text-sm text-gray-900 dark:text-gray-100 line-clamp-2">
                      {highlightMatch(getDisplayContent(msg.content), query)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
